const PDP_SELECTOR = 'main[data-template^="product."][data-template*="pdp-v2"]';
const PRODUCT_FORM_SELECTOR = 'product-form-component form[data-type="add-to-cart-form"]';
const CLOSURE_OPTION_SELECTOR = '[data-afd-closure-option-kind]';
const CLOSURE_FIELD_CLASS = 'afd-additional-closure-field';

const CLOSURE_OPTIONS_BY_HANDLE = new Map([
  [
    'cypress-one-shoulder-slit-sequin',
    {
      kind: 'cypress',
      bodyClass: 'afd-product-cypress-ring-request',
      checkboxLabel: 'Add optional lower ring',
      propertyName: 'properties[Additional ring]',
      propertyValue: 'Yes',
      analyticsEvent: 'cypress_ring_help_open',
    },
  ],
  [
    'cypress-after-dark-mini-sequin',
    {
      kind: 'cypress',
      bodyClass: 'afd-product-cypress-ring-request',
      checkboxLabel: 'Add optional lower ring',
      propertyName: 'properties[Additional ring]',
      propertyValue: 'Yes',
      analyticsEvent: 'cypress_ring_help_open',
      hideHelpLink: true,
    },
  ],
  [
    'saphira-one-shoulder-slit-velvet',
    {
      kind: 'saphira',
      bodyClass: 'afd-product-saphira-closure-request',
      checkboxLabel: 'Add optional lower crystal closure',
      propertyName: 'properties[Additional closure]',
      propertyValue: 'Yes',
      analyticsEvent: null,
      hideHelpLink: true,
    },
  ],
  [
    'saphira-nightshade-mini-velvet',
    {
      kind: 'saphira',
      bodyClass: 'afd-product-saphira-closure-request',
      checkboxLabel: 'Add optional lower crystal closure',
      propertyName: 'properties[Additional closure]',
      propertyValue: 'Yes',
      analyticsEvent: null,
      hideHelpLink: true,
    },
  ],
]);

const CLOSURE_BODY_CLASSES = Array.from(CLOSURE_OPTIONS_BY_HANDLE.values(), ({ bodyClass }) => bodyClass);

function isDirectTextBlock(element) {
  return element instanceof HTMLElement && element.matches('.text-block, rte-formatter.text-block');
}

function isBodyCopy(element) {
  return (
    element instanceof HTMLElement &&
    !element.matches(`dialog-component, .spacer-block, .${CLOSURE_FIELD_CLASS}, script, style`)
  );
}

export function getProductHandle(pathname = window.location.pathname) {
  const match = pathname.match(/^\/products\/([^/?#]+)/i);
  return match ? decodeURIComponent(match[1]) : '';
}

function getRoot(root = typeof document !== 'undefined' ? document : null) {
  if (!root) return null;
  return root.querySelector(PDP_SELECTOR);
}

function getClosureOptionElements(popup) {
  const innerContainer = popup?.parentElement || null;
  const innerGroup = popup?.closest('.group-block') || null;
  const outerGroup = innerGroup?.parentElement?.closest('.group-block') || innerGroup || null;
  const directChildren = Array.from(innerContainer?.children || []);
  const popupIndex = directChildren.indexOf(popup);
  const headingText = directChildren.slice(0, popupIndex).reverse().find(isDirectTextBlock) || null;
  const bodyText = directChildren.slice(popupIndex + 1).find(isBodyCopy) || null;

  return {
    headingText,
    bodyText,
    innerContainer,
    popup,
    innerGroup,
    outerGroup,
  };
}

function annotateClosureOption(popup, config) {
  const { headingText, bodyText, innerContainer, outerGroup } = getClosureOptionElements(popup);
  const popupButton = popup?.querySelector('.popup-link__button') || null;
  const popupInner = popup?.querySelector('.popup-link__inner') || null;
  const popupTextBlocks = Array.from(
    popupInner?.querySelectorAll('.text-block, rte-formatter.text-block') || []
  );

  outerGroup?.setAttribute('data-afd-closure-request', 'group');
  popup?.setAttribute('data-afd-closure-request', 'popup');
  popupButton?.setAttribute('data-afd-closure-request', 'popup-link');
  innerContainer?.setAttribute('data-afd-closure-request', 'container');
  headingText?.setAttribute('data-afd-closure-request-text', 'heading');
  bodyText?.setAttribute('data-afd-closure-request-text', 'body');
  popupTextBlocks[0]?.setAttribute('data-afd-closure-popup-text', 'heading');
  popupTextBlocks[1]?.setAttribute('data-afd-closure-popup-text', 'support');
  popup?.toggleAttribute('hidden', config.hideHelpLink === true);

  if (config.analyticsEvent) {
    popup?.setAttribute('data-afd-analytics-event', config.analyticsEvent);
    popupButton?.setAttribute('data-afd-analytics-event', config.analyticsEvent);
  } else {
    popup?.removeAttribute('data-afd-analytics-event');
    popupButton?.removeAttribute('data-afd-analytics-event');
  }
}

function removeCheckboxes(pdpRoot) {
  for (const field of pdpRoot.querySelectorAll(`.${CLOSURE_FIELD_CLASS}`)) {
    field.remove();
  }
}

function removeClosureOption(popup) {
  const { outerGroup } = getClosureOptionElements(popup);
  const spacer = outerGroup?.nextElementSibling;

  outerGroup?.remove();

  if (spacer instanceof HTMLElement && spacer.matches('.spacer-block')) {
    spacer.remove();
  }
}

function ensureCheckbox(pdpRoot, popup, config) {
  const productForm = pdpRoot.querySelector(PRODUCT_FORM_SELECTOR);
  const formId = productForm?.getAttribute('id') || '';
  const { innerContainer: container, outerGroup } = getClosureOptionElements(popup);

  if (!container || !outerGroup || !formId) {
    removeCheckboxes(pdpRoot);
    return;
  }

  let field = outerGroup.querySelector(`.${CLOSURE_FIELD_CLASS}`);
  if (!field) {
    field = document.createElement('label');
    field.className = `${CLOSURE_FIELD_CLASS} spacing-style`;
    field.setAttribute('data-afd-closure-request', 'checkbox');

    const input = document.createElement('input');
    input.type = 'checkbox';

    const label = document.createElement('span');
    label.textContent = config.checkboxLabel;

    field.append(input, label);
  }

  const input = field.querySelector('input');
  if (input instanceof HTMLInputElement) {
    input.name = config.propertyName;
    input.value = config.propertyValue;
    input.setAttribute('form', formId);
  }

  if (popup?.parentElement === container) {
    const popupNext = popup.nextElementSibling;
    if (field.parentElement !== container || field.previousElementSibling !== popup) {
      field.remove();
      container.insertBefore(field, popupNext);
    }
  } else if (field.parentElement !== container) {
    container.appendChild(field);
  }
}

export function applyPopupLinkCustomization(root = typeof document !== 'undefined' ? document : null) {
  const pdpRoot = getRoot(root);
  if (!pdpRoot) return;

  const productHandle = getProductHandle(window.AFD_PAGE_CONTEXT?.path || window.location.pathname);
  const config = CLOSURE_OPTIONS_BY_HANDLE.get(productHandle) || null;
  const popups = Array.from(pdpRoot.querySelectorAll(CLOSURE_OPTION_SELECTOR));
  let activePopup = null;

  for (const bodyClass of CLOSURE_BODY_CLASSES) {
    document.body?.classList.remove(bodyClass);
  }

  for (const popup of popups) {
    if (!(popup instanceof HTMLElement)) continue;

    const kind = popup.getAttribute('data-afd-closure-option-kind');
    if (config && kind === config.kind) {
      activePopup = popup;
      annotateClosureOption(popup, config);
    } else {
      removeClosureOption(popup);
    }
  }

  if (config && activePopup) {
    document.body?.classList.add(config.bodyClass);
    ensureCheckbox(pdpRoot, activePopup, config);
  } else {
    removeCheckboxes(pdpRoot);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyPopupLinkCustomization());
  } else {
    applyPopupLinkCustomization();
  }

  const observer = new MutationObserver(() => applyPopupLinkCustomization());
  observer.observe(document.documentElement, { childList: true, subtree: true });
}
